import type { ElementRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  input,
  signal,
  viewChild,
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'nr-range-slider',
  templateUrl: './range-slider.html',
  styleUrl: './range-slider.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RangeSliderComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {},
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RangeSliderComponent implements ControlValueAccessor {
  private static readonly PERCENTAGE_SCALE = 100;
  private static readonly ICON_COL_WIDTH = 24;
  private static readonly ICON_GAP = 12;
  private static readonly THUMB_WIDTH = 16;
  private static readonly MIDPOINT_RATIO = 0.5;
  private static readonly TOOLTIP_DECIMALS = 2;

  private readonly sliderRef = viewChild<ElementRef<HTMLInputElement>>('sliderInput');
  private readonly formDisabledState = signal(false);

  private onChange?: (val: number) => void;
  private onTouched?: () => void;

  readonly label = input<string | undefined>(undefined);
  readonly min = input<number | string>(0);
  readonly max = input<number | string>(100);
  readonly step = input<number | string | undefined>(undefined);
  readonly ariaLabel = input('');
  readonly iconStart = input<string | undefined>(undefined);
  readonly iconEnd = input<string | undefined>(undefined);
  readonly tooltipText = input<string | undefined>(undefined);
  /**
   * Forces the slider disabled regardless of form state — for callers with their own disable
   * condition (e.g. the footer progress slider in preview mode), unlike other sliders which stay
   * always active. Named `forceDisabled` rather than `disabled` because Angular's
   * `FormControlDirective` reserves the `disabled` input name on any element bound with
   * `[formControl]` and logs a dev-mode warning as soon as something else binds to it.
   */
  readonly forceDisabled = input(false);

  protected readonly value = signal(0);
  protected readonly isDisabled = computed(() => this.forceDisabled() || this.formDisabledState());
  protected readonly isTooltipVisible = signal(false);

  protected readonly resolvedTooltipText = computed(
    () =>
      this.tooltipText() ??
      String(parseFloat(this.value().toFixed(RangeSliderComponent.TOOLTIP_DECIMALS))),
  );

  protected readonly tooltipLeft = computed(() => {
    const min = Number(this.min());
    const max = Number(this.max());
    const val = this.value();
    const p = max === min ? RangeSliderComponent.MIDPOINT_RATIO : (val - min) / (max - min);

    const iconColWithGap = RangeSliderComponent.ICON_COL_WIDTH + RangeSliderComponent.ICON_GAP;
    const leftOffset = this.iconStart() !== undefined ? iconColWithGap : 0;
    const rightOffset = this.iconEnd() !== undefined ? iconColWithGap : 0;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const thumbHalf = RangeSliderComponent.THUMB_WIDTH / 2;
    const deductWidth = leftOffset + rightOffset + RangeSliderComponent.THUMB_WIDTH;

    return `calc(${leftOffset + thumbHalf}px + ${p} * (100% - ${deductWidth}px))`;
  });

  constructor() {
    effect(() => {
      const slider = this.sliderRef()?.nativeElement;
      if (!slider) return;
      const min = Number(this.min());
      const max = Number(this.max());
      const step = this.step();
      const val = this.value();
      slider.min = String(min);
      slider.max = String(max);
      if (step !== undefined) {
        slider.step = String(step);
      }
      slider.value = String(val);
      if (max !== min) {
        slider.style.setProperty(
          '--value',
          `${((val - min) * RangeSliderComponent.PERCENTAGE_SCALE) / (max - min)}%`,
        );
      }
    });
  }

  writeValue(val: number | null): void {
    this.value.set(val ?? 0);
  }

  registerOnChange(fn: (val: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabledState.set(isDisabled);
  }

  protected onInput(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) return;
    const val = Number(event.target.value);
    this.value.set(val);
    this.onChange?.(val);
  }

  protected onBlur(): void {
    this.onTouched?.();
    this.isTooltipVisible.set(false);
  }
}
